import notification from "antd/es/notification";
import { chunk } from "lodash-es";
import { defineMessages } from "react-intl";
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { Action, isActionOf } from "typesafe-actions";

import { geometryFromBinary } from "@mapmycustomers/shared";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { EntityTypesSupportedByMapsPage, Territory } from "@mapmycustomers/shared/types/entity";
import {
  BusinessRule,
  BusinessRuleAssignmentPayload,
  PlatformBusinessRule,
} from "@mapmycustomers/shared/types/entity/BusinessRule";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import { ListRequest } from "@mapmycustomers/shared/types/viewModel/internalModel/ListRequest";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";

import i18nService from "@app/config/I18nService";
import { getTerritoryBusinessRules } from "@app/scene/map/store/selectors";
import { CreateTerritoryPayload } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import { addExtraCoordinateToTerritoryDetail } from "@app/store/api/utils/territories";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganization, getOrganizationId } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import {
  areTerritoriesLoading,
  getTerritories,
  getTerritoriesHavingShapeFetched,
  haveAnyTerritories,
} from "@app/store/territories/selectors";
import { findDifference } from "@app/util/arrayUtils";
import { VISIBLE_TERRITORIES_LIMIT } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";
import territoryFieldModel from "@app/util/fieldModel/TerritoryFieldModel";
import loggingService from "@app/util/logging";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import getBoundaryUrl from "@app/util/map/getBoundaryUrl";
import getTerritoryPayload from "@app/util/territory/getTerritoryPayload";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  createTerritory,
  deleteTerritory,
  ensureTerritoriesHaveShape,
  fetchAllTerritories,
  fetchTerritories,
  fetchTerritoryShapes,
  hideTerritories,
  showTerritories,
  updateTerritory,
  updateTerritorySharing,
} from "./actions";

const messages = defineMessages({
  created: {
    id: "territories.create.success",
    defaultMessage: "{name} Territory Created.",
    description: "Territory created successfully",
  },
  deleted: {
    id: "territories.deleted.success",
    defaultMessage: "Territory successfully deleted",
    description: "Territory deleted successfully",
  },
  updated: {
    id: "territories.update.success",
    defaultMessage: "{name} Territory Edited.",
    description: "Territory updated successfully",
  },
});

const successTypeActionMatcher =
  () =>
  (action: Action): boolean =>
    isActionOf(fetchTerritories.success, action);

export function* onFetchTerritories({ payload }: ReturnType<typeof fetchTerritories.request>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const requestPayload: Partial<Omit<PlatformListRequest, "$columns">> = {
      $filters: payload.request?.filter
        ? convertToPlatformFilterModel(payload.request.filter, [], territoryFieldModel)
        : ({} as PlatformFilterModel),
      $limit: payload.request?.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : undefined,
      $offset: payload.request?.range ? payload.request.range.startRow : 0,
      $order: payload.request?.sort ? convertToPlatformSortModel(payload.request.sort) : undefined,
    };
    requestPayload.$filters!.includeAccessStatus = true;

    const response: ListResponse<Territory> = yield callApi(
      "fetchTerritories",
      organizationId,
      requestPayload
    );

    yield put(
      fetchTerritories.success({
        territories: response.data.map((territory) => ({ ...territory, count: undefined })),
        total: response.accessible,
      })
    );

    // Send follow-up request with additional parameter to calculate number of items per territory
    // This is kind of optimistic fetching to let quicker display list of territories first
    requestPayload.$filters!.includeEntitiesCount = true;
    const responseWithCount: ListResponse<Territory> = yield callApi(
      "fetchTerritories",
      organizationId,
      requestPayload
    );

    yield put(
      fetchTerritories.success({
        countOnly: true,
        territories: responseWithCount.data,
        total: responseWithCount.accessible,
      })
    );
  } catch (error) {
    yield put(fetchTerritories.failure(error));
  }
}

export function* onFetchAllTerritories({
  payload,
}: ReturnType<typeof fetchAllTerritories.request>) {
  try {
    const { force = false } = payload ?? {};

    const loading: boolean = yield select(areTerritoriesLoading);
    const anyTerritories: boolean = yield select(haveAnyTerritories);

    if (loading && !force) {
      // just return. Saga which initiated loading will trigger fetchAllTerritories.success
      return;
    }

    // We have territories already, don't fetch again, unless force==true.
    // Note: I know this condition doesn't work good when there are no territories at all,
    // but in that case there's nothing to return and no shape files to load. So it
    // should be acceptable.
    if (anyTerritories && !force) {
      yield put(fetchAllTerritories.success());
      return;
    }

    // call fetchCustomFields actions with a given request options
    const request: Partial<ListRequest> = {
      range: { endRow: 10000, startRow: 0 }, // fetch all
      sort: [{ field: territoryFieldModel.getByName("name")!, order: SortOrder.ASC }],
    };
    yield put(fetchTerritories.request({ request }));

    // now wait till all succeed or any fails, whichever happens first
    const { failure } = yield race({
      failure: take([fetchTerritories.failure]),
      success: all([take(successTypeActionMatcher())]),
    });

    if (failure) {
      yield put(fetchAllTerritories.failure());
    } else {
      yield put(fetchAllTerritories.success());
    }
  } catch (error) {
    yield put(fetchAllTerritories.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateTerritory({
  payload: {
    businessRules,
    onFailure,
    onSuccess,
    shape,
    suppressErrorNotification,
    territory,
    userIdsToShareWith,
  },
}: ReturnType<typeof createTerritory.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const currentUserId: User["id"] = yield select(getCurrentUserId);

    const payload: CreateTerritoryPayload = yield call(getTerritoryPayload, {
      ...territory,
      userId: currentUserId,
    });

    const response: Response = yield fetch(getBoundaryUrl("countryStates"), {
      body: JSON.stringify(payload.territoryDetail),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
    if (response.ok) {
      payload.territoryDetail.region = yield response.json();
    }

    const newTerritory: Territory = yield callApi("createTerritory", org.id, payload, shape);

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.created, {
          name: newTerritory.name,
        }),
      });
    }

    if (userIdsToShareWith?.length) {
      const payload = userIdsToShareWith.map((user) => ({
        groupId: newTerritory.id,
        userId: user,
      }));
      yield callApi("bulkCreateGroupShare", org.id, payload);
    }

    // There's a timeout after shared-territories api requests and the moment territory is actually updated.
    // so, we're using a workaround for now. For more info: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1663156032349549
    newTerritory.userIds = userIdsToShareWith ?? [];

    // fake shape
    newTerritory.territoryDetail.shape = shape;

    const filteredBusinessRules = businessRules?.filter((ruleSet) => !ruleSet.isSystemGenerated);
    const systemGeneratedRuleSet = businessRules?.find((ruleSet) => ruleSet.isSystemGenerated);

    if (filteredBusinessRules && filteredBusinessRules.length) {
      const createdBusinessRules: PlatformBusinessRule[] = yield callApi(
        "bulkCreateTerritoryBusinessRules",
        org.id,
        filteredBusinessRules.map((ruleSet) => ({
          ...ruleSet,
          filters: convertMapFilterToPlatformFilterModel(
            ruleSet.filters,
            "universal" in ruleSet.filters
              ? MAP_ENTITY_TYPES
              : (Object.keys(ruleSet.filters) as EntityTypesSupportedByMapsPage[])
          ),
          territoryIds: [newTerritory.id],
        }))
      );

      const assignments: BusinessRuleAssignmentPayload[] = createdBusinessRules
        .map((rule, index) => {
          const addedUserIds =
            (filteredBusinessRules[index].territories ?? []).find(
              ({ territoryId }) => territoryId === -1
            )?.userIds ?? []; // territoryId is -1 at creation

          return {
            addedUserIds,
            removedUserIds: [], // no users removed at creation
            ruleId: rule.id,
            territoryId: newTerritory.id,
          };
        })
        .filter(({ addedUserIds }) => addedUserIds.length);

      if (assignments.length) {
        yield callApi("bulkUpsertTerritoryBusinessRulesAssignments", org.id, assignments);
      }
    }

    // Ideally it should be always there
    if (systemGeneratedRuleSet) {
      const currentBusinessRules: BusinessRule[] = yield select(getTerritoryBusinessRules);
      const currentSystemGeneratedRule = currentBusinessRules[0];

      if (currentSystemGeneratedRule) {
        const addedUserIds =
          (systemGeneratedRuleSet.territories ?? []).find(({ territoryId }) => territoryId === -1)
            ?.userIds ?? []; // territoryId is -1 at creation

        if (addedUserIds.length) {
          const assignment: BusinessRuleAssignmentPayload = {
            addedUserIds,
            removedUserIds: [],
            ruleId: systemGeneratedRuleSet.id!,
            territoryId: newTerritory.id,
          };

          yield callApi("bulkUpsertTerritoryBusinessRulesAssignments", org.id, [assignment]);
        }
      }
    }

    onSuccess(newTerritory);
    yield put(createTerritory.success({ territory: newTerritory }));
  } catch (error) {
    yield put(createTerritory.failure(error));
    yield put(handleError({ displayNotification: !suppressErrorNotification, error }));
    if (onFailure) {
      yield call(onFailure);
    }
  }
}
export function* onUpdateTerritory({
  payload: {
    onFailure,
    onSuccess,
    shape,
    suppressErrorNotification,
    territory,
    userIdsToShareWith,
  },
}: ReturnType<typeof updateTerritory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const payload = { ...territory };
    const response: Response = yield fetch(getBoundaryUrl("countryStates"), {
      body: JSON.stringify(addExtraCoordinateToTerritoryDetail(territory.territoryDetail)),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });
    if (response.ok) {
      payload.territoryDetail = { ...payload.territoryDetail, region: yield response.json() };
    }

    const updatedTerritory: Territory = yield callApi("updateTerritory", orgId, payload, shape);

    if (userIdsToShareWith) {
      const [usersToAdd, usersToDelete] = findDifference(
        territory.userIds ?? [],
        userIdsToShareWith
      );

      const createPayload = (userIds: User["id"][]) =>
        userIds.map((userId) => ({ groupId: territory.id, userId }));

      yield all([
        usersToAdd.length
          ? callApi("bulkCreateGroupShare", orgId, createPayload(usersToAdd))
          : undefined,
        usersToDelete.length
          ? callApi("bulkDeleteGroupShare", orgId, createPayload(usersToDelete))
          : undefined,
      ]);
    }

    // There's a timeout after shared-territories api requests and the moment territory is actually updated.
    // so, we're using a workaround for now. For more info: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1663156032349549
    updatedTerritory.userIds = userIdsToShareWith ?? [];

    // fake shape
    updatedTerritory.territoryDetail.shape = shape ?? territory.territoryDetail.shape;

    yield put(updateTerritory.success({ territory: updatedTerritory }));
    if (onSuccess) {
      yield call(onSuccess, updatedTerritory);
    }
  } catch (error) {
    yield put(updateTerritory.failure());
    yield put(handleError({ displayNotification: !suppressErrorNotification, error }));
    if (onFailure) {
      yield call(onFailure);
    }
  }
}

export function* onDeleteTerritory({
  payload: { onSuccess, territoryId },
}: ReturnType<typeof deleteTerritory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteTerritory", orgId, territoryId);
    yield put(deleteTerritory.success({ territoryId }));
    if (onSuccess) {
      yield call(onSuccess, territoryId);
    }
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.deleted),
      });
    }
  } catch (error) {
    yield put(deleteTerritory.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateTerritorySharing({
  payload: { onFailure, onSuccess, suppressErrorNotification, territory, userIdsToShareWith },
}: ReturnType<typeof updateTerritorySharing.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const usersToAdd = userIdsToShareWith.filter((id) => !territory.userIds?.includes(id));
    const usersToDelete = (territory.userIds ?? []).filter(
      (id) => !userIdsToShareWith.includes(id)
    );
    const createPayload = (userIds: User["id"][]) =>
      userIds.map((userId) => ({ groupId: territory.id, userId }));

    yield all([
      usersToAdd.length
        ? callApi("bulkCreateGroupShare", orgId, createPayload(usersToAdd))
        : undefined,
      usersToDelete.length
        ? callApi("bulkDeleteGroupShare", orgId, createPayload(usersToDelete))
        : undefined,
    ]);

    const updatedTerritory: Territory = yield callApi("fetchTerritory", orgId, territory.id);
    // There's a timeout after shared-territories api requests and the moment territory is actually updated.
    // so, we're using a workaround for now. For more info: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1663156032349549
    updatedTerritory.userIds = userIdsToShareWith;

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.updated, {
          name: updatedTerritory.name,
        }),
      });
    }

    yield put(updateTerritorySharing.success({ territory: updatedTerritory }));
    if (onSuccess) {
      yield call(onSuccess, updatedTerritory);
    }
  } catch (error) {
    yield put(updateTerritorySharing.failure(error));
    yield put(handleError({ displayNotification: !suppressErrorNotification, error }));
    if (onFailure) {
      yield call(onFailure);
    }
  }
}

export function* onShowTerritories({
  payload: visibleTerritoryIds,
}: ReturnType<typeof showTerritories>) {
  const territories: Territory[] = yield select(getTerritories);
  const visibleTerritoryIdsSet = new Set(visibleTerritoryIds);
  const hiddenTerritoryIds = territories
    .filter(({ id }) => !visibleTerritoryIdsSet.has(id))
    .map(({ id }) => id);
  yield put(hideTerritories(hiddenTerritoryIds));
}

export function* onEnsureTerritoriesHaveShape({
  payload: territories,
}: ReturnType<typeof ensureTerritoriesHaveShape>) {
  const territoryIds: Territory["id"][] = yield select(getTerritoriesHavingShapeFetched);

  const territoriesToProcess = territories.filter(
    ({ id, territoryDetail }) =>
      territoryDetail.type !== "points" &&
      !territoryDetail.shape &&
      territoryDetail.shapeFile?.publicURI &&
      !territoryIds.includes(id)
  );

  if (territoriesToProcess.length) {
    yield put(fetchTerritoryShapes.request(territoriesToProcess));
  }
}

export function* onFetchTerritoryShapes({
  payload: territories,
}: ReturnType<typeof fetchTerritoryShapes.request>) {
  try {
    if (!territories.length) {
      yield put(fetchTerritoryShapes.success([]));
      return;
    }

    // One chunk per screenful of territories, deliberately: chunks are fetched and dispatched serially,
    // and the caller sorts displayable territories ahead of hidden ones, so sizing the chunk to the
    // display limit means everything the user can actually see arrives in the first round.
    //
    // Chunking smaller costs twice over. Every `success` replaces the territories array, so all the work
    // keyed on it — the area sort, the GeoJSON rebuild, the hover bbox index, the level-of-detail snap and
    // deck.gl's tessellation — re-runs for every territory loaded so far, not just the new ones. And the
    // serial loop turns one network round into several. Measured on 300 territories at 4x CPU throttling:
    // 20.5s of main-thread work in chunks of 100 against 14.8s in one chunk, and all shapes loaded at 3.1s
    // against 9s. The only thing chunks of 100 won was showing the first 100 territories about 1.7s
    // earlier, which is a partial map rather than a useful one.
    const chunks = chunk(territories, VISIBLE_TERRITORIES_LIMIT);

    for (const chunk of chunks) {
      const files: SettleResult<ArrayBuffer>[] = yield allSettled(
        chunk.map(function* (territory) {
          const response: Response = yield fetch(
            territory.territoryDetail.shapeFile!.publicURI!,
            {}
          );
          const arrayBuffer: ArrayBuffer = yield response.arrayBuffer();
          return arrayBuffer;
        })
      );

      const result = chunk.map((territory, index) => {
        if (!files[index].error) {
          try {
            const shape = geometryFromBinary(files[index].result as ArrayBuffer);
            return {
              ...territory,
              territoryDetail: { ...territory.territoryDetail, shape },
            } as Territory;
          } catch (error) {
            loggingService.error(`Failed to decode territory (id=${territory.id}) shape file`, {
              error,
            });
          }
        }
        return territory;
      });

      yield put(fetchTerritoryShapes.success(result));
    }
  } catch (error) {
    yield put(fetchTerritoryShapes.failure(territories));
    yield handleError({ error });
  }
}

export function* onPersistHiddenTerritoriesIds({
  payload: hiddenTerritoriesIds,
}: ReturnType<typeof hideTerritories>) {
  yield put(updateMetadata.request({ hiddenTerritoriesIds }));
}

const regularFetchActionMatcher = (action: Action): boolean =>
  isActionOf(fetchAllTerritories.request, action) && (!action.payload || !action.payload.force);

const forcedFetchActionMatcher = (action: Action): boolean =>
  isActionOf(fetchAllTerritories.request, action) && !!action.payload?.force;

export function* territoriesSaga() {
  yield takeLatest(fetchTerritories.request, onFetchTerritories);
  yield takeLeading(regularFetchActionMatcher, onFetchAllTerritories);
  yield takeLatest(forcedFetchActionMatcher, onFetchAllTerritories);
  yield takeEvery(createTerritory.request, onCreateTerritory);
  yield takeEvery(updateTerritory.request, onUpdateTerritory);
  yield takeEvery(deleteTerritory.request, onDeleteTerritory);
  yield takeEvery(updateTerritorySharing.request, onUpdateTerritorySharing);
  yield takeEvery(hideTerritories, onPersistHiddenTerritoriesIds);
  yield takeEvery(showTerritories, onShowTerritories);
  yield takeEvery(ensureTerritoriesHaveShape, onEnsureTerritoriesHaveShape);
  yield takeEvery(fetchTerritoryShapes.request, onFetchTerritoryShapes);
}
